import {
  BufferGeometry, CatmullRomCurve3, Float32BufferAttribute, Group,
  Mesh, MeshStandardMaterial, Vector3, type ShaderMaterial,
} from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { applyHullFinish } from './HullFinish';

export function grid(columns: number, rows: number, point: (u: number, v: number) => Vector3) {
  const positions: number[] = [], uv: number[] = [], indices: number[] = [];
  for (let row = 0; row <= rows; row++) for (let col = 0; col <= columns; col++) {
    const u = col / columns, v = row / rows; positions.push(...point(u, v).toArray()); uv.push(u, 1 - v);
    if (row < rows && col < columns) { const a = row * (columns + 1) + col, c = a + columns + 1; indices.push(a, c, a + 1, a + 1, c, c + 1); }
  }
  const geometry = new BufferGeometry(); geometry.setAttribute('position', new Float32BufferAttribute(positions, 3)); geometry.setAttribute('uv', new Float32BufferAttribute(uv, 2)); geometry.setIndex(indices); geometry.computeVertexNormals(); return geometry;
}

/** Rounded rectangular rail profile swept along a real 3D curve. */
export function rail(points: Vector3[], width: number, depth: number, segments = 44) {
  const curve = new CatmullRomCurve3(points), vertices: number[] = [], indices: number[] = [];
  const section = [[-.5, -.28], [-.28, -.5], [.28, -.5], [.5, -.28], [.5, .28], [.28, .5], [-.28, .5], [-.5, .28]];
  for (let i = 0; i <= segments; i++) {
    const p = curve.getPoint(i / segments), tangent = curve.getTangent(i / segments);
    const side = new Vector3(tangent.y, -tangent.x, 0).normalize(), normal = tangent.clone().cross(side).normalize();
    for (const [x, z] of section) vertices.push(...p.clone().addScaledVector(side, x * width).addScaledVector(normal, z * depth).toArray());
    if (i < segments) for (let j = 0; j < 8; j++) { const a = i * 8 + j, b = i * 8 + (j + 1) % 8; indices.push(a, b, a + 8, b, b + 8, a + 8); }
  }
  for (let i = 1; i < 7; i++) { indices.push(0, i + 1, i); const n = segments * 8; indices.push(n, n + i, n + i + 1); }
  const geometry = new BufferGeometry(); geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3)); geometry.setIndex(indices); geometry.computeVertexNormals(); return geometry;
}

export type CabinPanel = { geometry: BufferGeometry; samples: Vector3[]; aspect: number };
export type CabinAssembly = { root: Group; panel: CabinPanel; glow: MeshStandardMaterial; compact: boolean; windshield?: ShaderMaterial; stick: Group; throttle: Group };

/** Shared mesh tools for one responsive cabin architecture. */
export function createCabinBuilder(aspect: number) {
  const root = new Group(); root.name = 'AURORA / survey vessel interior';
  const p = (x: number, y: number, d: number) => new Vector3(x * aspect * d, y * d, -d);
  const materials = {
    shell: new MeshStandardMaterial({ color: '#1c2a30', metalness: .42, roughness: .48 }),
    armor: new MeshStandardMaterial({ color: '#96a8aa', metalness: .68, roughness: .31 }),
    seal: new MeshStandardMaterial({ color: '#0c151a', metalness: .05, roughness: .82 }),
    detail: new MeshStandardMaterial({ color: '#4b6269', metalness: .72, roughness: .38 }),
    // Champagne-white emission keeps the previous linear luminance and bloom.
    glow: new MeshStandardMaterial({ color: '#f2e9d8', emissive: '#767269', emissiveIntensity: 6.3, metalness: .18, roughness: .32 }),
    amber: new MeshStandardMaterial({ color: '#c4a173', emissive: '#c79a58', emissiveIntensity: .24, metalness: .58, roughness: .4 }),
  };
  materials.shell.name = 'anodized';
  type MaterialKey = keyof typeof materials;
  const batches = new Map<MaterialKey, BufferGeometry[]>();
  const add = (geometry: BufferGeometry, key: MaterialKey) => {
    const flat = geometry.index ? geometry.toNonIndexed() : geometry;
    if (flat !== geometry) geometry.dispose();
    flat.deleteAttribute('uv'); if (!batches.has(key)) batches.set(key, []); batches.get(key)!.push(flat);
  };
  // Closed, bevelled structural plates. Hard face normals preserve their thickness.
  const plate = (outline: Vector3[], key: MaterialKey, thickness = .08, bevel = .018) => {
    let points = outline.map(point => point.clone());
    let normal = points[1].clone().sub(points[0]).cross(points[2].clone().sub(points[0])).normalize();
    if (normal.z < 0) { points = points.reverse(); normal.negate(); }
    const center = points.reduce((sum, point) => sum.add(point), new Vector3()).divideScalar(points.length);
    const inset = points.map(point => point.clone().lerp(center, .035).addScaledVector(normal, bevel));
    const back = points.map(point => point.clone().addScaledVector(normal, -thickness));
    const positions: number[] = [];
    const tri = (a: Vector3, b: Vector3, c: Vector3) => positions.push(...a.toArray(), ...b.toArray(), ...c.toArray());
    for (let i = 1; i < points.length - 1; i++) { tri(inset[0], inset[i], inset[i + 1]); tri(back[0], back[i + 1], back[i]); }
    for (let i = 0; i < points.length; i++) {
      const j = (i + 1) % points.length;
      tri(points[i], points[j], inset[j]); tri(points[i], inset[j], inset[i]);
      tri(back[i], back[j], points[j]); tri(back[i], points[j], points[i]);
    }
    const geometry = new BufferGeometry(); geometry.setAttribute('position', new Float32BufferAttribute(positions, 3)); geometry.computeVertexNormals(); add(geometry, key);
  };
  const part = (geometry: BufferGeometry, key: MaterialKey, parent: Group) => {
    const mesh = new Mesh(geometry, materials[key]); mesh.frustumCulled = false; parent.add(mesh); return mesh;
  };
  const finish = () => {
    for (const [key, geometries] of batches) {
      const combined = mergeGeometries(geometries)!; geometries.forEach(geometry => geometry.dispose());
      const mesh = new Mesh(combined, materials[key]); mesh.name = `cabin_${key}`; mesh.frustumCulled = false; root.add(mesh);
    }
    applyHullFinish(root, false);
    return { root, glow: materials.glow };
  };
  return { root, p, add, plate, part, finish };
}
